import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2 } from "lucide-react";
import { 
    fetchDataApi,
    createFunctionApi,
    deleteFunctionApi,
} from '../../services/functionServices';
import { fetchAllGroupApi } from 'src/services/permissionServices';

interface Function {
    id: string;
    name: string;
    description?: string;
    createdAt: string;
}

interface Group {
    id: string;
    name: string;
    description?: string;
}

interface FunctionManagerProps {
    onGroupSelect?: (groupId: string) => void;
}

const FunctionManager: React.FC<FunctionManagerProps> = ({ onGroupSelect }) => {
    const [functions, setFunctions] = useState<Function[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<string>("");
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newFunctionName, setNewFunctionName] = useState('');
    const [newFunctionDescription, setNewFunctionDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const showToast = (msg: string) => window.alert(msg);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [functionsResponse, groupsResponse] = await Promise.all([
                fetchDataApi('functions/getAll'),
                fetchAllGroupApi()
            ]);
            setFunctions(functionsResponse.data || []);
            const groupsData = groupsResponse.data || [];
            setGroups(groupsData);
            if (groupsData.length > 0 && !selectedGroupId) {
                setSelectedGroupId(groupsData[0].id);
                onGroupSelect?.(groupsData[0].id);
            }
            setLoading(false);
        } catch (error: any) {
            setError(true);
            setLoading(false);
            showToast(error.message || "Failed to fetch data");
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleGroupChange = (groupId: string) => {
        setSelectedGroupId(groupId);
        onGroupSelect?.(groupId);
    };

    const handleCreateFunction = async () => {
        if (!newFunctionName.trim()) {
            showToast("Please enter a name for the function.");
            return;
        }
        try {
            await createFunctionApi({
                name: newFunctionName.trim(),
                description: newFunctionDescription.trim() || undefined
            });
            showToast(`${newFunctionName} has been created successfully.`);
            setNewFunctionName('');
            setNewFunctionDescription('');
            setCreateModalOpen(false);
            fetchData(); // Refresh the list after creation
        } catch (error: any) {
            showToast(error.message || "Failed to create the function. Please try again.");
        }
    };

    const handleDeleteFunction = async (functionId: string) => {
        try {
            await deleteFunctionApi(functionId);
            setFunctions(prev => prev.filter(f => f.id !== functionId));
            showToast("Function has been removed successfully.");
        } catch (error: any) {
            showToast(error.message || "Failed to delete the function. Please try again.");
        }
    };

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center" style={{ height: 256 }}>Loading...</div>;
    }

    if (error) {
        return <div className="d-flex justify-content-center align-items-center text-danger" style={{ height: 256 }}>Error loading data</div>;
    }

    return (
        <div className="mb-4">
            <div className="bg-white  rounded p-4"  style={{ 
    borderRadius: "8px",
    border: "1px solidrgb(216, 217, 219)", // Tailwind's gray-200
    background: "#fff",
  
   
  }}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="h5 text-primary fw-semibold d-flex align-items-center mb-0">
                        <Users style={{ width: 20, height: 20, marginRight: 8 }} />
                        Business Functions ({functions.length})
                    </div>
                    <button
                        className="btndoweload text-white d-flex align-items-center"
                        onClick={() => setCreateModalOpen(true)}
                        type="button"
                    >
                        <Plus style={{ width: 18, height: 18, marginRight: 8 }} />
                        Create Function
                    </button>
                </div>
                {createModalOpen && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.4)' }} className="d-flex align-items-center justify-content-center">
                        <div className="bg-white rounded  w-100" style={{ maxWidth: 420, padding: 24, position: 'relative' }}>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h2 className="h5 fw-bold mb-0">Create New Function</h2>
                                <button
                                    onClick={() => setCreateModalOpen(false)}
                                    aria-label="Close"
                                    type="button"
                                    style={{ fontSize: 24, fontWeight: 700, background: 'none', border: 'none', outline: 'none', boxShadow: 'none', color: '#222', padding: 0, lineHeight: 1, cursor: 'pointer' }}
                                >
                                    ×
                                </button>
                            </div>
                            <div>
                                <div className="mb-3">
                                    <label htmlFor="function-name" className="form-label">Function Name *</label>
                                    <input
                                        id="function-name"
                                        className="form-control"
                                        placeholder="e.g., Digital Banking, Risk Management"
                                        value={newFunctionName}
                                        onChange={(e) => setNewFunctionName(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFunction()}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="function-description" className="form-label">Description (Optional)</label>
                                    <input
                                        id="function-description"
                                        className="form-control"
                                        placeholder="Brief description of the function's responsibilities"
                                        value={newFunctionDescription}
                                        onChange={(e) => setNewFunctionDescription(e.target.value)}
                                    />
                                </div>
                                <div className="d-flex justify-content-end gap-2">
                                    <button
                                        className="btn btn-cancle"
                                        onClick={() => setCreateModalOpen(false)}
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-create text-white"
                                        onClick={handleCreateFunction}
                                        type="button"
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="mt-4">
                    {functions.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            No functions created yet. Create your first function to get started.
                        </div>
                    ) : (
                        <div className="row g-3">
                            {functions.map((func) => (
                                <div key={func.id} className="col-12 col-md-4">
                                    {/* Replace with FunctionCard if available, else use existing card markup */}
                                    {/* <FunctionCard ... /> */}
                                  <div 
  className="card h-100 position-relative" 
  style={{ 
    borderRadius: "8px",
    border: "1px solid #e5e7eb", // Tailwind's gray-200
    background: "#fff",
  
    overflow: "hidden" // Ensures border-radius clips child elements
  }}
>
  {/* Left green accent tab (absolute positioning) */}
  <div 
   style={{
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "6px",
      backgroundColor:" #f79d53",
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
      zIndex: 1
    }}
  />

  {/* Card header with title, description, and delete button */}
  <div 
    className="card-body pb-2 pt-4 px-4 border-bottom" 
    style={{ 
      borderTopLeftRadius: "8px",
      borderTopRightRadius: "8px",
      borderColor: "#e5e7eb" // Matches card border
    }}
  >
    <button
      type="button"
      onClick={() => handleDeleteFunction(func.id)}
      className="btn btn-link text-danger position-absolute end-0 top-0 mt-2 me-2 p-0"
      title="Delete Function"
    >
      <Trash2 style={{ width: 20, height: 20 }} />
    </button>

    <h4 className="fw-bold text-dark mb-0">{func.name}</h4>
    {func.description && (
      <p className="small text-muted mt-1 mb-0">{func.description}</p>
    )}
  </div>

  
</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FunctionManager;